import Controller from "controllers/application_controller"
import { html, render } from "lit-html"
import { keyed } from "lit-html/directives/keyed.js"
import {
  addDays,
  addMonths,
  dayOfMonth,
  endOfWeek,
  isSameMonth,
  monthWeeks,
  parse,
  startOfMonth,
  startOfWeek
} from "../lib/date_utils"

const captionFormat = new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric", timeZone: "UTC" })
const dayLabelFormat = new Intl.DateTimeFormat("en-US", { dateStyle: "full", timeZone: "UTC" })

/**
 * Single-date calendar grid (see calendar.css for the DOM contract and scope).
 * The server renders the nav and weekday row; this controller owns the
 * visible month and the active day, renders the weeks with lit-html, and
 * implements the APG date grid keyboard model with a roving tabindex.
 *
 * Selecting a day updates `selected` and dispatches `ui-calendar:select`. The
 * calendar does not own a form field: Ui::DatePicker (or any other host)
 * listens for the event and commits the value.
 */
export default class extends Controller {
  static identifier = "ui-calendar"
  static targets = ["previous", "next", "caption", "body"]
  static values = { selected: String, today: String, min: String, max: String }

  connect() {
    this.addEventListener(this.previousTarget, "click", () => this.showMonth(addMonths(this.month, -1)))
    this.addEventListener(this.nextTarget, "click", () => this.showMonth(addMonths(this.month, 1)))
    this.addEventListener(this.bodyTarget, "click", event => this.dayClicked(event))
    this.addEventListener(this.bodyTarget, "keydown", event => this.keydown(event))

    this.activate(this.defaultDate)
    this.render()
  }

  selectedValueChanged() {
    if (!this.rendered) return

    this.activate(this.defaultDate)
    this.render()
  }

  /** Returns to the selected month (or today's) and focuses the active day. */
  show() {
    this.moveTo(this.defaultDate)
  }

  // Re-renders here rather than waiting for selectedValueChanged, which
  // Stimulus calls asynchronously, so focus lands on the new grid.
  select(date) {
    this.selectedValue = date
    this.show()
    this.dispatch("select", { detail: { date } })
  }

  get defaultDate() {
    return this.selectedValue || this.todayValue
  }

  activate(date) {
    this.activeDate = this.clamp(date)
    this.month = startOfMonth(this.activeDate)
  }

  moveTo(date) {
    this.activate(date)
    this.render()
    this.bodyTarget.querySelector(`button[data-date="${this.activeDate}"]:not([data-outside-month])`)?.focus()
  }

  // APG: a month you navigate to keeps the selected day or today active when
  // it holds one, else its first day.
  showMonth(month) {
    const activeDate = [this.selectedValue, this.todayValue].find(date => date && isSameMonth(date, month)) || month

    this.month = month
    this.activeDate = this.clamp(activeDate)
    this.render()
  }

  dayClicked(event) {
    const day = event.target.closest("button[data-date]")
    if (day) this.select(day.dataset.date)
  }

  keydown(event) {
    const day = event.target.closest("button[data-date]")
    if (!day) return

    const target = this.keyboardTarget(event, day.dataset.date)
    if (!target) return

    event.preventDefault()
    this.moveTo(target)
  }

  keyboardTarget(event, date) {
    switch (event.key) {
      case "ArrowLeft":
        return addDays(date, -1)
      case "ArrowRight":
        return addDays(date, 1)
      case "ArrowUp":
        return addDays(date, -7)
      case "ArrowDown":
        return addDays(date, 7)
      case "Home":
        return startOfWeek(date)
      case "End":
        return endOfWeek(date)
      case "PageUp":
        return addMonths(date, -this.pageMonths(event))
      case "PageDown":
        return addMonths(date, this.pageMonths(event))
    }
  }

  pageMonths(event) {
    if (event.shiftKey) {
      return 12
    } else {
      return 1
    }
  }

  // Keyboard focus stops at the selectable range, so it never lands on a
  // disabled day.
  clamp(date) {
    if (this.minValue && date < this.minValue) return this.minValue
    if (this.maxValue && date > this.maxValue) return this.maxValue
    return date
  }

  isOutOfRange(date) {
    return this.clamp(date) !== date
  }

  render() {
    this.rendered = true
    this.captionTarget.textContent = captionFormat.format(parse(this.month))
    this.previousTarget.disabled = Boolean(this.minValue && startOfMonth(this.minValue) >= this.month)
    this.nextTarget.disabled = Boolean(this.maxValue && startOfMonth(this.maxValue) <= this.month)

    // Keyed by month: reusing day buttons across months would carry their
    // colour transitions, so the old selection would fade on a new date.
    render(keyed(this.month, monthWeeks(this.month).map(week => html`
      <tr>${week.map(date => this.dayTemplate(date))}</tr>
    `)), this.bodyTarget)
  }

  dayTemplate(date) {
    const selected = date === this.selectedValue
    const today = date === this.todayValue

    return html`
      <td data-slot="calendar-day" role="gridcell" aria-selected=${selected}>
        <button
          type="button"
          data-slot="calendar-day-button"
          data-date=${date}
          tabindex=${this.tabIndex(date)}
          aria-label=${dayLabelFormat.format(parse(date))}
          aria-current=${today && "date"}
          ?data-selected=${selected}
          ?data-current=${today}
          ?data-outside-month=${!isSameMonth(date, this.month)}
          ?disabled=${this.isOutOfRange(date)}
        >${dayOfMonth(date)}</button>
      </td>
    `
  }

  tabIndex(date) {
    if (date === this.activeDate) {
      return 0
    } else {
      return -1
    }
  }
}
